/**
 * ReadinessDrawer — the defensibility view. Full raw → normalized → weight →
 * contribution trail per input, missing-input reasons, fallback markers,
 * profile version and compute timestamp.
 */
import { Badge } from '../../components/ui/badge';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../../components/ui/sheet';
import { ScoreRing, StateChip } from './ReadinessBoardTable';

const INPUT_LABELS = {
  attendance: 'Attendance %',
  classic_assessments: 'Assessment scores',
  online_assessments: 'Online assessments',
  live_quiz: 'Live quiz performance',
  skill_matrix: 'Skill matrix ratings',
  learning: 'Learning completion',
  assignments: 'Assignment grades',
};
const INPUT_ORDER = Object.keys(INPUT_LABELS);

export default function ReadinessDrawer({ trainee, thresholds, onClose }) {
  if (!trainee) return null;
  const inputs = trainee.inputs || {};
  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="readiness-drawer">
        <SheetHeader>
          <SheetTitle className="font-display">{trainee.name}</SheetTitle>
          <SheetDescription>
            Readiness breakdown — based on {trainee.coverage.label} inputs
          </SheetDescription>
        </SheetHeader>

        <div className="flex items-center gap-4 mt-4">
          <ScoreRing score={trainee.score} state={trainee.state} size={64} />
          <div className="space-y-1">
            <StateChip state={trainee.state} />
            {trainee.escalated && (
              <p className="text-xs text-amber-600">Escalated to at-risk — under 20% of batch time left</p>
            )}
            {thresholds && (
              <p className="text-xs text-muted-foreground">
                Floor-ready ≥ {thresholds.floor_ready} · On-track ≥ {thresholds.on_track}
              </p>
            )}
          </div>
        </div>

        <div className="mt-5 space-y-2" data-testid="readiness-input-breakdown">
          {INPUT_ORDER.filter((k) => inputs[k]).map((k) => {
            const inp = inputs[k];
            return (
              <div key={k} className="rounded-lg border p-3" data-testid={`input-row-${k}`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">{INPUT_LABELS[k]}</p>
                  {inp.missing ? (
                    <Badge variant="outline" className="bg-slate-50 text-slate-500 text-[10px]">Missing</Badge>
                  ) : (
                    <span className="text-sm font-semibold">{inp.raw}</span>
                  )}
                </div>
                {inp.missing ? (
                  <p className="text-xs text-muted-foreground mt-1" data-testid={`input-reason-${k}`}>
                    {inp.reason} — weight redistributed
                  </p>
                ) : (
                  <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                    <span>raw {inp.raw}</span>
                    <span>· weight {inp.weight_used}</span>
                    <span>· contributes <strong className="text-foreground">{inp.contribution ?? '—'}</strong> pts</span>
                    <span className="ml-auto">{inp.n_datapoints} datapoint{inp.n_datapoints === 1 ? '' : 's'}</span>
                  </div>
                )}
                {inp.source === 'mapped_fallback' && (
                  <Badge variant="outline"
                         className="mt-1.5 bg-violet-50 text-violet-700 border-violet-200 text-[10px]"
                         data-testid={`fallback-badge-${k}`}>
                    Fallback: derived from mapped assessment record (original attempt rows missing)
                  </Badge>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-[11px] text-muted-foreground mt-4" data-testid="readiness-drawer-meta">
          Profile v{trainee.profile_version} · computed {new Date(trainee.computed_at).toLocaleString()}
          {' '}({trainee.compute_reason})
        </p>
      </SheetContent>
    </Sheet>
  );
}
